import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useTransition,
} from "react"

import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import Stack from "@mui/system/Stack"
import { MessageSquareText, Rocket, Mail } from "lucide-react"
import { useTranslation } from "react-i18next"

import ContactSupportButton from "@components/ContactSupportButton"
import { IconWrapper } from "@components/IconWrapper"
import { MinimizableCard } from "@components/MinimizableCard"

import { CONTACT_EMAIL } from "@constants/globalStrings"

import { SubMenu } from "./SubMenu"

import type { Theme } from "@mui/system/createTheme"
import type { SystemStyleObject } from "@mui/system/styleFunctionSx"

const SupportSection: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Stack sx={{ p: theme.spacing(1) }}>
      <MinimizableCard localStorageKey="digit-support-section-minimized">
        <Stack
          direction="column"
          gap={theme.spacing(1)}
          justifyContent="flex-end"
          flex={1}
        >
          <Stack
            direction="column"
            gap={theme.spacing(0.5)}
            className="minimize-hidden"
            flex={1}
          >
            <Typography
              alignItems="center"
              color={theme.palette.text.primary}
              display="flex"
              gap={theme.spacing(0.5)}
              variant="body2"
            >
              <IconWrapper size={16} sx={{ color: theme.palette.text.primary }}>
                <Rocket />
              </IconWrapper>
              {t("components:helpTitle")}
            </Typography>
            <Typography
              variant="body1"
              color={theme.palette.text.secondary}
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                hyphens: "auto",
                textWrap: "balance",
              }}
            >
              {t("components:helpSubtitle")}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            gap={theme.spacing(0.5)}
            flex={1}
            sx={{
              mb: "-1px", // to account for button borders
            }}
          >
            <ContactSupportButton
              startIcon={
                <IconWrapper size={16}>
                  <MessageSquareText />
                </IconWrapper>
              }
              size="small"
              sx={{ width: "fit-content" }}
            >
              {t("components:helpChat")}
            </ContactSupportButton>
            <Button
              href={`mailto:${CONTACT_EMAIL}`}
              rel="noopener noreferrer"
              startIcon={
                <IconWrapper size={16}>
                  <Mail />
                </IconWrapper>
              }
              size="small"
              target="_blank"
              variant="text"
            >
              {t("components:helpEmail")}
            </Button>
          </Stack>
        </Stack>
      </MinimizableCard>
    </Stack>
  )
}

const getVerticalFadeStyles = ({
  isAllTheWayToTheTop,
  isAllTheWayToTheBottom,
}: {
  isAllTheWayToTheTop: boolean
  isAllTheWayToTheBottom: boolean
}) => {
  if (!isAllTheWayToTheTop && !isAllTheWayToTheBottom) {
    return {
      maskImage:
        "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0px, rgba(255, 255, 255, 1) 8px, rgba(255, 255, 255, 1) calc(100% - 8px), rgba(255, 255, 255, 0) 100%)",
      WebkitMaskImage:
        "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0px, rgba(255, 255, 255, 1) 8px, rgba(255, 255, 255, 1) calc(100% - 8px), rgba(255, 255, 255, 0) 100%)",
    }
  }

  if (!isAllTheWayToTheTop) {
    return {
      maskImage:
        "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0px, rgba(255, 255, 255, 1) 8px, rgba(255, 255, 255, 1) 100%)",
      WebkitMaskImage:
        "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0px, rgba(255, 255, 255, 1) 8px, rgba(255, 255, 255, 1) 100%)",
    }
  }

  if (!isAllTheWayToTheBottom) {
    return {
      maskImage:
        "linear-gradient(to bottom, rgba(255, 255, 255, 1) 0px, rgba(255, 255, 255, 1) calc(100% - 8px), rgba(255, 255, 255, 0) 100%)",
      WebkitMaskImage:
        "linear-gradient(to bottom, rgba(255, 255, 255, 1) 0px, rgba(255, 255, 255, 1) calc(100% - 8px), rgba(255, 255, 255, 0) 100%)",
    }
  }

  return {
    maskImage:
      "linear-gradient(to bottom, rgba(255, 255, 255, 1) 0px, rgba(255, 255, 255, 1) 100%)",
    WebkitMaskImage:
      "linear-gradient(to bottom, rgba(255, 255, 255, 1) 0px, rgba(255, 255, 255, 1) 100%)",
  }
}

const useVerticalScroll = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [, startTransition] = useTransition()
  const theme = useTheme()
  const [fadeStyles, setFadeStyles] = useState(
    getVerticalFadeStyles({
      isAllTheWayToTheTop: false,
      isAllTheWayToTheBottom: false,
    }),
  )

  const updateFadeStyles = useCallback(() => {
    startTransition(() => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          scrollContainerRef.current
        const scrollPosition = Math.round(scrollTop + clientHeight)
        const threshold = Number(theme.spacing(1)) // 8px

        const isAllTheWayToTheTop = scrollTop <= threshold
        const isAllTheWayToTheBottom =
          Math.abs(scrollPosition - scrollHeight) <= threshold

        setFadeStyles(
          getVerticalFadeStyles({
            isAllTheWayToTheTop,
            isAllTheWayToTheBottom,
          }),
        )
      }
    })
  }, [theme])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener("scroll", updateFadeStyles)
      container.addEventListener("resize", updateFadeStyles)
      updateFadeStyles()
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", updateFadeStyles)
        container.removeEventListener("resize", updateFadeStyles)
      }
    }
  }, [updateFadeStyles])

  return { scrollContainerRef, fadeStyles }
}

export interface SidebarMenuProps {
  subMenus: SubMenu[]
  orgSwitcher?: React.ReactNode
  stackSx?: SystemStyleObject<Theme>
  showSupportSection?: boolean
}

const ScrollableContent: React.FC<{ subMenus: SubMenu[] }> = ({ subMenus }) => {
  const { scrollContainerRef, fadeStyles } = useVerticalScroll()

  return (
    <Stack
      ref={scrollContainerRef}
      direction="column"
      gap={2}
      py={1}
      sx={{
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        ...fadeStyles,
      }}
    >
      {subMenus.map(({ translatedTextKey: titleKey, items, id }) => (
        <SubMenu translatedTextKey={titleKey} items={items} key={id} />
      ))}
    </Stack>
  )
}

const FooterSection: React.FC<{ showSupportSection: boolean }> = ({
  showSupportSection,
}) => {
  if (!showSupportSection) return null

  return (
    <Stack direction="column" gap={1} height="auto">
      <SupportSection />
    </Stack>
  )
}

export const SidebarMenu: React.FC<SidebarMenuProps> = ({
  subMenus,
  orgSwitcher = null,
  showSupportSection = false,
}) => {
  return (
    <Stack
      display="flex"
      direction="column"
      gap={1}
      sx={{
        p: 1,
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.default,
        height: "100%",
        zIndex: 1,
        border: 0,
        width: (theme) => theme.spacing(28),
      }}
      data-testid="app-drawer-menu"
    >
      {orgSwitcher && (
        <Stack direction="column" height="auto">
          {orgSwitcher}
        </Stack>
      )}
      <ScrollableContent subMenus={subMenus} />
      <FooterSection showSupportSection={showSupportSection} />
    </Stack>
  )
}
